import { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";

const bandRate = (classRate, band) => {
  const n = band - 60;
  const f = n >= 0 ? Math.pow(1.05, n) : Math.pow(0.95, -n);
  return Math.round((Number(classRate) * Math.round(f * 1e5) / 1e5 + 1e-9) * 100) / 100;
};

export function WsibProfile({ items, onChange, ratesYear, onRatesYear }) {
  const list = items || [];
  const [lib, setLib] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  useEffect(() => { api.get("/wsib/classes?year=2026").then((r) => setLib(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (q.trim().length < 3) { setHits([]); return; }
    const t = setTimeout(() => api.get(`/wsib/codes?year=2026&q=${encodeURIComponent(q.trim())}`).then((r) => setHits(r.data.codes.slice(0, 12))).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const byCode = useMemo(() => Object.fromEntries((lib?.classes || []).map((c) => [c.code, c])), [lib]);
  const pickFromCode = (hit) => {
    const c = byCode[hit.class];
    if (!c) return;
    if (list.some((w) => String(w.code).toUpperCase() === c.code)) { toast.info(`${c.code} is already in the profile`); }
    else { onChange([...list, { code: c.code, description: c.description, class_rate: c.rate, risk_band: 60, rate: c.rate, naics: hit.code }]); toast.success(`Added class ${c.code} — ${c.description} (from ${hit.code} ${hit.description})`); }
    setQ(""); setHits([]);
  };

  const upd = (i, patch) => { const a = [...list]; a[i] = { ...a[i], ...patch }; onChange(a); };
  const del = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { code: "", description: "", class_rate: 0, risk_band: 60, rate: 0 }]);
  const setCode = (i, raw) => {
    const code = raw.toUpperCase().trim();
    const c = byCode[code];
    const band = list[i].risk_band || 60;
    if (c) upd(i, { code, description: c.description, class_rate: c.rate, risk_band: band, rate: bandRate(c.rate, band) });
    else upd(i, { code: raw });
  };
  const setBand = (i, band) => {
    const w = list[i]; const cr = Number(w.class_rate ?? byCode[w.code]?.rate ?? w.rate);
    upd(i, { risk_band: band, class_rate: cr, rate: bandRate(cr, band) });
  };
  const importStandard = () => {
    if (!lib) return;
    const existing = new Set(list.map((x) => String(x.code).trim()));
    const merged = [...list, ...lib.classes.filter((c) => !existing.has(c.code)).map((c) => ({ code: c.code, description: c.description, class_rate: c.rate, risk_band: 60, rate: c.rate }))];
    onChange(merged);
    toast.success(`Imported ${lib.classes.length} WSIB 2026 classes at the class rate (risk band 60)`);
  };
  const bands = []; for (let b = 83; b >= 1; b--) bands.push(b);

  return (
    <div className="border-t border-border pt-3" data-testid="wsib-profile">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">WSIB profile · 2026 class &amp; risk band rates</span>
        <div className="flex gap-2">
          <button type="button" data-testid="company-import-wsib" onClick={importStandard} className="text-xs border border-black px-2 py-1 bg-black text-white hover:bg-neutral-800 transition-colors">Import 2026 WSIB classes</button>
          <button type="button" data-testid="company-add-wsib" onClick={add} className="text-xs border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors">+ Add</button>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mb-2">
        Pick the class from the business's WSIB premium rate statement, then its <b>risk band</b>. Band 60 is the class rate; each band is ≈5% above or below the one beside it (max band 83, minimum premium $0.07).
        Effective rate = class rate × band factor, per $100 of insurable earnings{lib ? ` (2026 ceiling $${Number(lib.max_insurable).toLocaleString()})` : ""}. The Workers' Compensation and WSIB Reconciliation reports use the effective rate.
      </p>
      <div className="relative mb-2" data-testid="wsib-code-lookup">
        <input data-testid="wsib-code-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find the class by business activity — e.g. roofing, dental office, trucking, software…" className="w-full border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
        {hits.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 border border-border bg-white shadow-lg max-h-64 overflow-y-auto" data-testid="wsib-code-results">
            {hits.map((h) => (
              <button type="button" key={h.code} data-testid={`wsib-code-hit-${h.code}`} onClick={() => pickFromCode(h)} className="w-full text-left px-3 py-2 text-xs hover:bg-neutral-100 flex items-center gap-3 border-b border-border last:border-0">
                <span className="font-mono text-muted-foreground w-14">{h.code}</span>
                <span className="flex-1 min-w-0 truncate">{h.description}</span>
                <span className="font-mono font-semibold w-8">{h.class}</span>
                <span className="text-muted-foreground w-44 truncate">{byCode[h.class]?.description}</span>
                <span className="tabular-nums w-12 text-right">{byCode[h.class] ? byCode[h.class].rate.toFixed(2) : ""}</span>
              </button>
            ))}
          </div>
        )}
        {q.trim().length >= 3 && hits.length === 0 && <div className="text-[10px] text-muted-foreground mt-1">No classification code matches "{q}".</div>}
      </div>
      {onRatesYear && (
        <label className="flex items-center gap-2 text-[11px] text-muted-foreground mb-2">
          <input type="checkbox" data-testid="wsib-rates-year-confirmed" className="accent-black h-3.5 w-3.5" checked={Number(ratesYear || 0) >= 2026} onChange={(e) => onRatesYear(e.target.checked ? 2026 : null)} />
          Confirmed against the 2026 WSIB premium rate statement (silences the Nov/Dec reminder for 2026)
        </label>
      )}
      <div className="max-h-72 overflow-y-auto pr-1 space-y-2">
        {list.map((w, i) => {
          const cls = byCode[String(w.code).toUpperCase()];
          const cr = Number(w.class_rate ?? cls?.rate ?? 0);
          const band = Number(w.risk_band || 60);
          const factor = band >= 60 ? Math.pow(1.05, band - 60) : Math.pow(0.95, 60 - band);
          return (
            <div key={i} data-testid={`company-wsib-${i}`} className="border border-border bg-neutral-50/60 p-2">
              <div className="flex items-center gap-2">
                <input data-testid={`company-wsib-code-${i}`} list="wsib-class-list" placeholder="Class (e.g. G5)" value={w.code} onChange={(e) => setCode(i, e.target.value)} className="w-24 border border-input bg-white px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black" />
                <input data-testid={`company-wsib-desc-${i}`} placeholder="Description" value={w.description || ""} onChange={(e) => upd(i, { description: e.target.value })} className="flex-1 min-w-0 border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                <label className="text-[10px] text-muted-foreground whitespace-nowrap">Class rate
                  <input data-testid={`company-wsib-class-rate-${i}`} type="number" step="0.01" value={cr} onChange={(e) => { const v = parseFloat(e.target.value) || 0; upd(i, { class_rate: v, rate: bandRate(v, band) }); }} className="ml-1 w-20 border border-input bg-white px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
                </label>
                <label className="text-[10px] text-muted-foreground whitespace-nowrap">Risk band
                  <select data-testid={`company-wsib-band-${i}`} value={band} onChange={(e) => setBand(i, parseInt(e.target.value, 10))} className="ml-1 w-44 border border-input bg-white px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-black">
                    {bands.map((b) => { const r = bandRate(cr, b); if (b < 60 && r < 0.07) return null; return <option key={b} value={b}>{b} · {b === 60 ? "class" : (b > 60 ? `+${b - 60}` : b - 60)} · {r.toFixed(2)}</option>; })}
                  </select>
                </label>
                <label className="text-[10px] text-muted-foreground whitespace-nowrap">Effective %
                  <input data-testid={`company-wsib-rate-${i}`} type="number" step="0.01" value={w.rate} onChange={(e) => upd(i, { rate: parseFloat(e.target.value) || 0 })} className="ml-1 w-20 border border-black bg-white px-2 py-1.5 text-sm font-semibold text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
                </label>
                <button type="button" data-testid={`company-wsib-bands-${i}`} onClick={() => setOpen(open === i ? null : i)} className="text-xs border border-border px-2 py-1.5 hover:bg-black hover:text-white transition-colors" title="Risk band table">⋯</button>
                <button type="button" data-testid={`company-wsib-del-${i}`} onClick={() => del(i)} className="text-xs border border-border px-2 py-1.5 hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">×</button>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                <span data-testid={`company-wsib-factor-${i}`}>Factor {factor.toFixed(5)} → ${bandRate(cr, band).toFixed(2)} per $100</span>
                {cls?.naics_groups?.length ? <span data-testid={`company-wsib-naics-${i}`}>NAICS {cls.naics_groups.map((g) => g.naics).join(", ")}: {cls.naics_groups.map((g) => g.label).join(" · ")}</span> : null}
              </div>
              {open === i && <RiskBandTable code={w.code} classRate={cr} selected={band} onPick={(b) => { setBand(i, b); setOpen(null); }} />}
            </div>
          );
        })}
      </div>
      <datalist id="wsib-class-list">{(lib?.classes || []).map((c) => <option key={c.code} value={c.code}>{c.description} — {c.rate.toFixed(2)}</option>)}</datalist>
    </div>
  );
}

function RiskBandTable({ code, classRate, selected, onPick }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/wsib/classes/${encodeURIComponent(code || "X")}/risk-bands?year=2026&class_rate=${classRate}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [code, classRate]);
  if (!data) return <div className="mt-2 text-[10px] text-muted-foreground">Loading risk bands…</div>;
  return (
    <div className="mt-2 border border-border bg-white max-h-56 overflow-y-auto" data-testid="wsib-risk-band-table">
      <table className="w-full text-[11px] tabular-nums">
        <thead className="sticky top-0 bg-neutral-100 text-[9px] uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-2 py-1">Band</th><th className="text-right px-2 py-1">vs. 60</th><th className="text-right px-2 py-1">Index from</th><th className="text-right px-2 py-1">Index to</th><th className="text-right px-2 py-1">Factor</th><th className="text-right px-2 py-1">$ / $100</th></tr>
        </thead>
        <tbody>
          {data.bands.map((b) => (
            <tr key={b.band} onClick={() => onPick(b.band)} data-testid={`wsib-band-row-${b.band}`} className={`cursor-pointer hover:bg-neutral-100 ${b.band === selected ? "bg-black text-white hover:bg-black" : b.band === 60 ? "bg-neutral-50 font-semibold" : ""}`}>
              <td className="px-2 py-0.5">{b.band}</td><td className="text-right px-2 py-0.5">{b.label}</td>
              <td className="text-right px-2 py-0.5">{b.index_from.toFixed(5)}</td><td className="text-right px-2 py-0.5">{b.index_to == null ? "and above" : b.index_to.toFixed(5)}</td>
              <td className="text-right px-2 py-0.5">{b.factor.toFixed(5)}</td><td className="text-right px-2 py-0.5 font-semibold">{b.rate.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
